// src/App.tsx
import React, { useEffect, useState, Suspense } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom'

import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { useSubscriptionStore } from './store/subscriptionStore'

import ScrollToTop from './components/ScrollToTop'
import GoogleAnalytics from './GoogleAnalytics'
import { useSwipeGesture } from './hooks/useSwipeGesture'
import PrivateRoute from './components/PrivateRoute'
import AdminRoute from './components/AdminRoute'
import UserProfileRouter from './components/UserProfileRouter'
import { Toaster } from './components/ui/toast'
import { prefetchBottomNavRoutes } from './lib/prefetchRoutes'
import QuickPlayTransition from './pages/QuickPlayTransition'

// lazy pages
const LandingPage       = React.lazy(() => import('./pages/LandingPage'))
const Features          = React.lazy(() => import('./pages/Features'))
const Privacy           = React.lazy(() => import('./pages/Privacy'))
const Terms             = React.lazy(() => import('./pages/Terms'))
const InvitePage        = React.lazy(() => import('./pages/InvitePage'))
const Community         = React.lazy(() => import('./pages/Community'))
const Contact           = React.lazy(() => import('./pages/Contact'))
const ResetPassword     = React.lazy(() => import('./pages/ResetPassword'))
const OnboardingFlow    = React.lazy(() => import('./components/onboarding/OnboardingFlow'))

const Dashboard         = React.lazy(() => import('./pages/Dashboard'))
const Search            = React.lazy(() => import('./pages/Search'))
const MatchMaking       = React.lazy(() => import('./pages/MatchMaking'))
const SocialFeed        = React.lazy(() => import('./components/social/SocialFeed'))
const QuickPlayWrapper  = React.lazy(() => import('./pages/QuickPlayWrapper'))
const FacilityBooking   = React.lazy(() => import('./pages/FacilityBooking'))
const Forums            = React.lazy(() => import('./pages/Forums'))
const ForumCategory     = React.lazy(() => import('./pages/ForumCategory'))
const ForumTopic        = React.lazy(() => import('./pages/ForumTopic'))
const NewForumTopic     = React.lazy(() => import('./pages/NewForumTopic'))
const ClubForumCategory = React.lazy(() => import('./pages/ClubForumCategory'))
const ClubForumIndex    = React.lazy(() => import('./pages/ClubForumIndex'))
const ClubProfile       = React.lazy(() => import('./pages/ClubProfile'))
const Training          = React.lazy(() => import('./pages/Training'))
const Events            = React.lazy(() => import('./pages/Events'))
const Messages          = React.lazy(() => import('./pages/Messages'))
const Profile           = React.lazy(() => import('./pages/Profile'))
const Activities        = React.lazy(() => import('./pages/Activities'))
const Leaderboard       = React.lazy(() => import('./pages/Leaderboard'))
const Settings          = React.lazy(() => import('./pages/Settings'))
const AdminDashboard    = React.lazy(() => import('./pages/AdminDashboard'))

import SettingsSkeleton       from './components/settings/SettingsSkeleton'
import AdminDashboardSkeleton from './components/admin/AdminDashboardSkeleton'
import ClubProfileSkeleton    from './components/clubs/ClubProfileSkeleton'
import ClubForumSkeleton      from './components/forums/ClubForumSkeleton'

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const { initializeSubscription, cleanupSubscription } = useSubscriptionStore()
  const location = useLocation()

  useSwipeGesture(location.pathname.toLowerCase().startsWith('/dashboard/facilities'))

  useEffect(() => {
    if (isAuthenticated) {
      initializeSubscription().catch(console.error)
      return () => cleanupSubscription()
    }
  }, [isAuthenticated, initializeSubscription, cleanupSubscription])

  useEffect(() => {
    if (isAuthenticated !== undefined) {
      const splash = document.getElementById('splash-screen')
      if (splash) {
        splash.classList.add('fade-out')
        setTimeout(() => splash.remove(), 400)
      }
      const warm = () => prefetchBottomNavRoutes()
      if ('requestIdleCallback' in window) {
        ;(window as any).requestIdleCallback(warm, { timeout: 2500 })
      } else {
        setTimeout(warm, 2000)
      }
    }
  }, [isAuthenticated])

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Public */}
        <Route
          path="/"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />
          }
        />
        <Route path="/landing"        element={<LandingPage />} />
        <Route path="/features"       element={<Features />} />
        <Route path="/privacy"        element={<Privacy />} />
        <Route path="/terms"          element={<Terms />} />
        <Route path="/invite"         element={<InvitePage />} />
        <Route path="/contact"        element={<Contact />} />
        <Route path="/community"      element={<Community />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding"     element={<OnboardingFlow onComplete={() => {}} />} />

        {/* Protected */}
        <Route path="/dashboard/feed"      element={<PrivateRoute><SocialFeed /></PrivateRoute>} />
        <Route path="/dashboard"           element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/search"    element={<PrivateRoute><Search /></PrivateRoute>} />
        <Route path="/dashboard/match"     element={<PrivateRoute><MatchMaking /></PrivateRoute>} />
        <Route path="/dashboard/quick-play"            element={<PrivateRoute><QuickPlayWrapper /></PrivateRoute>} />
        <Route path="/dashboard/quick-play-transition" element={<PrivateRoute><QuickPlayTransition /></PrivateRoute>} />

        {/* === Profile routes all go through the router === */}
        <Route
          path="/dashboard/trainers/:id"
          element={
            <PrivateRoute>
              <UserProfileRouter />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/members/:id"
          element={
            <PrivateRoute>
              <UserProfileRouter />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard/profile/:id"
          element={
            <PrivateRoute>
              <UserProfileRouter />
            </PrivateRoute>
          }
        />

        <Route path="/dashboard/forums"                element={<PrivateRoute><Forums /></PrivateRoute>} />
        <Route path="/dashboard/forums/category/:slug" element={<PrivateRoute><ForumCategory /></PrivateRoute>} />
        <Route path="/dashboard/forums/topic/:id"      element={<PrivateRoute><ForumTopic /></PrivateRoute>} />
        <Route path="/dashboard/forums/new-topic"      element={<PrivateRoute><NewForumTopic /></PrivateRoute>} />

        <Route
          path="/dashboard/clubs/:clubId"
          element={
            <PrivateRoute>
              <Suspense fallback={<ClubProfileSkeleton />}>
                <ClubProfile />
              </Suspense>
            </PrivateRoute>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<ClubForumSkeleton />}>
                <ClubForumIndex />
              </Suspense>
            }
          />
          <Route
            path="forum/category/:slug"
            element={
              <Suspense fallback={<ClubForumSkeleton />}>
                <ClubForumCategory />
              </Suspense>
            }
          />
          <Route
            path="forum/topic/:id"
            element={
              <Suspense fallback={<ClubForumSkeleton />}>
                <ForumTopic />
              </Suspense>
            }
          />
          <Route
            path="forum/new-topic"
            element={
              <Suspense fallback={<ClubForumSkeleton />}>
                <NewForumTopic />
              </Suspense>
            }
          />
        </Route>

        <Route path="/dashboard/facilities" element={<PrivateRoute><FacilityBooking /></PrivateRoute>} />
        <Route path="/dashboard/training"   element={<PrivateRoute><Training /></PrivateRoute>} />
        <Route path="/dashboard/events"     element={<PrivateRoute><Events /></PrivateRoute>} />
        <Route path="/dashboard/messages"   element={<PrivateRoute><Messages /></PrivateRoute>} />
        <Route path="/dashboard/profile"    element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/dashboard/activities" element={<PrivateRoute><Activities /></PrivateRoute>} />
        <Route path="/dashboard/leaderboard" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />

        <Route
          path="/dashboard/settings"
          element={
            <PrivateRoute>
              <Suspense fallback={<SettingsSkeleton />}>
                <Settings />
              </Suspense>
            </PrivateRoute>
          }
        />

        <Route
          path="/dashboard/admin"
          element={
            <AdminRoute>
              <Suspense fallback={<AdminDashboardSkeleton />}>
                <AdminDashboard />
              </Suspense>
            </AdminRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

const AppContent: React.FC = () => {
  const location = useLocation()
  const [isTransitioning, setIsTransitioning] = useState(false)

  useEffect(() => {
    if (location.pathname === '/dashboard/quick-play-transition') {
      setIsTransitioning(true)
      const timer = setTimeout(() => setIsTransitioning(false), 3500)
      return () => clearTimeout(timer)
    }
  }, [location.pathname])

  return (
    <>
      <GoogleAnalytics />
      <ScrollToTop />
      {location.pathname === '/dashboard/quick-play-transition' && isTransitioning && (
        <QuickPlayTransition />
      )}
      <AppRoutes />
      <Toaster />
    </>
  )
}

const App: React.FC = () => {
  useEffect(() => {
    import('./components/facilities/FacilityManagement')
  }, [])

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router basename="/">
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
